import { useQuery } from '@tanstack/react-query'

import { createApiCall } from 'services'
import type { CertificateAdmin } from 'types'

type UseCertificateGroupCandidatesParams = {
  query?: string
  limit?: number
}

type CertificatesResponse = {
  data: CertificateAdmin[]
  total: number
  limit: number
  offset: number
}

type UseCertificateGroupCandidatesResult = {
  data: CertificateAdmin[]
  isLoading: boolean
}

// VRG-337 — a self-contained (React Query) certificate search for the "Group
// certificates" member picker. Deliberately NOT `hooks/api/useCertificates`
// (the legacy redux-thunk hook backing the Certificates LIST page): that hook
// writes into the single global `state.certificates.certificates` slice, so
// using it here would overwrite the underlying list page's own data with the
// picker's own filtered/limited results for as long as this modal is open —
// exactly the shared-mutable-state collision `useProducts`/`useCertificateGroup`
// already avoid by being scoped React Query caches instead. This hook follows
// that same (safe) pattern.
export const useCertificateGroupCandidates = (
  params: UseCertificateGroupCandidatesParams,
  options?: { enabled?: boolean },
): UseCertificateGroupCandidatesResult => {
  const query = useQuery({
    queryKey: ['certificates', 'group-candidates', params],
    enabled: options?.enabled !== false,
    queryFn: async () => {
      const response = await createApiCall({
        method: 'GET',
        url: '/certificates/admin',
        params: {
          query: params.query || undefined,
          limit: params.limit ?? 50,
          offset: 0,
        },
      })
      return response.data as CertificatesResponse
    },
    placeholderData: previousData => previousData,
  })

  return {
    data: query.data?.data ?? [],
    isLoading: query.isLoading,
  }
}
